import { useState, useCallback, useEffect } from 'react';
import { Loader2, Copy, Check, ExternalLink, Clock, Zap, X, ChevronDown, ChevronRight, Brain, MessageSquare, RotateCw, Users, Bot } from 'lucide-react';
import { Link } from 'react-router-dom';
import Badge, { getStatusVariant } from '@/components/ui/Badge';
import { JsonContent } from '@/components/ui/json-content';
import { MarkdownContent } from '@/components/ui/markdown-content';
import { ToolCallCard } from '@/components/assistant-ui/tool-call-card';
import { spaceApi } from '../../services/api';
import { usePlatformEvents } from '../../contexts/PlatformEventContext';
import { routes } from '../../lib/routes';
import { hasBuildInfo, parseBuildTime } from '../../lib/build-info';
import { formatElapsed } from '../../utils/format';
import { isJsonLike } from '../../utils/format';
import { formatRelativeTime } from '../activity/utils';
import { fetchOffloadedContent } from '../../utils/s3-offload';
import type { RunSummary } from '../../types/runs';
import type { RunRuntime, RunStatusResponse, RunStepResponse } from '../../types';

interface RunDetailPanelProps {
  run: RunSummary | null;
  // The space that owns this run, resolved from the agent. Optional because
  // /me/runs doesn't carry spaceId — we resolve it from /me/agents. When
  // missing (orphan agent), detail-fetch / retry / agent-link degrade.
  runSpaceId?: string;
  onClose: () => void;
}

// ─── Date formatting ────────────────────────────────────────────────────────

function formatFullDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

// ─── Sub-agent grouping ─────────────────────────────────────────────────────

interface SubAgentRunInfo {
  agentName: string;
  status: string;
  durationMs?: number;
  responseText?: string;
  error?: string;
  toolCallCount?: number;
  childRunId?: string;
  prompt?: string;
}

interface ProcessedStep {
  kind: 'reasoning' | 'narration' | 'tool_call' | 'subagent-group';
  step?: RunStepResponse;
  subAgents?: SubAgentRunInfo[];
  elapsedMs: number;
}

function processSteps(steps: RunStepResponse[], runStartTime: number): ProcessedStep[] {
  const result: ProcessedStep[] = [];
  let i = 0;

  while (i < steps.length) {
    const step = steps[i];
    const rawElapsed = new Date(step.timestamp).getTime() - runStartTime;
    const elapsedMs = Number.isFinite(rawElapsed) ? rawElapsed : 0;

    if (step.type === 'tool_call' && step.toolName === '__subagent_spawn') {
      // Build a map of agentId→prompt from the spawn tasks for fallback prompt lookup
      const spawnTasks = (step.input as Record<string, unknown>)?.tasks as Array<{ agentId: string; prompt: string }> | undefined;
      const promptByAgentId = new Map(spawnTasks?.map((t) => [t.agentId, t.prompt]) ?? []);
      const usedAgentPrompts = new Set<string>();

      // Collect subsequent __subagent_run steps
      const subAgents: SubAgentRunInfo[] = [];
      i++;
      while (i < steps.length && steps[i].type === 'tool_call' && steps[i].toolName === '__subagent_run') {
        const runStep = steps[i];
        let parsed: Record<string, unknown> = {};
        try {
          const raw = runStep.output ?? runStep.error;
          if (raw) parsed = typeof raw === 'string' ? JSON.parse(raw) : (raw as Record<string, unknown>);
        } catch { /* ignore */ }

        const inputData = runStep.input as Record<string, unknown> | undefined;
        const agentId = inputData?.agentId as string | undefined;
        // Prompt from subagent_returned backfill, or fall back to spawn tasks
        let prompt = inputData?.prompt as string | undefined;
        if (!prompt && agentId && promptByAgentId.has(agentId) && !usedAgentPrompts.has(agentId)) {
          prompt = promptByAgentId.get(agentId);
          usedAgentPrompts.add(agentId);
        }
        // childRunId from input (subagent_started) or output (subagent_completed)
        const childRunId = (inputData?.childRunId as string) ?? (parsed.childRunId as string) ?? undefined;

        subAgents.push({
          agentName: (parsed.agentName as string) ?? runStep.toolCallId ?? 'Sub-Agent',
          status: (parsed.status as string) ?? (runStep.error ? 'failed' : 'completed'),
          durationMs: (parsed.durationMs as number) ?? runStep.durationMs,
          responseText: parsed.responseText as string | undefined,
          error: (parsed.error as string) ?? runStep.error,
          toolCallCount: (parsed.toolCalls as unknown[])?.length,
          childRunId,
          prompt,
        });
        i++;
      }
      result.push({ kind: 'subagent-group', subAgents, elapsedMs });
    } else if (step.type === 'tool_call' && step.toolName === '__subagent_run') {
      // Orphaned subagent_run (no spawn) — still render as sub-agent
      let parsed: Record<string, unknown> = {};
      try {
        const raw = step.output ?? step.error;
        if (raw) parsed = typeof raw === 'string' ? JSON.parse(raw) : (raw as Record<string, unknown>);
      } catch { /* ignore */ }

      const inputData = step.input as Record<string, unknown> | undefined;
      const childRunId = (inputData?.childRunId as string) ?? (parsed.childRunId as string) ?? undefined;
      const prompt = inputData?.prompt as string | undefined;

      result.push({
        kind: 'subagent-group',
        subAgents: [{
          agentName: (parsed.agentName as string) ?? step.toolCallId ?? 'Sub-Agent',
          status: (parsed.status as string) ?? (step.error ? 'failed' : 'completed'),
          durationMs: (parsed.durationMs as number) ?? step.durationMs,
          responseText: parsed.responseText as string | undefined,
          error: (parsed.error as string) ?? step.error,
          toolCallCount: (parsed.toolCalls as unknown[])?.length,
          childRunId,
          prompt,
        }],
        elapsedMs,
      });
      i++;
    } else {
      result.push({ kind: step.type as 'reasoning' | 'narration' | 'tool_call', step, elapsedMs });
      i++;
    }
  }

  return result;
}

// ─── Trace timing label ─────────────────────────────────────────────────────

function TraceTime({ elapsedMs, durationMs }: { elapsedMs: number; durationMs?: number }) {
  const elapsed = Number.isFinite(elapsedMs) ? (elapsedMs / 1000).toFixed(1) : null;
  const duration = durationMs != null && Number.isFinite(durationMs) ? (durationMs / 1000).toFixed(1) : null;
  if (!elapsed && !duration) return null;
  return (
    <span className="shrink-0 text-xs text-muted-foreground font-mono tabular-nums">
      {duration ? `${duration}s` : ''}{duration && elapsed ? ' · ' : ''}{elapsed ? `${elapsed}s` : ''}
    </span>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────

export default function RunDetailPanel({ run, runSpaceId, onClose }: RunDetailPanelProps) {
  const [copiedId, setCopiedId] = useState(false);
  const [copiedResponse, setCopiedResponse] = useState(false);
  const [copiedSteps, setCopiedSteps] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [fetchedRun, setFetchedRun] = useState<RunStatusResponse | null>(null);
  // Bumped by live run events to re-pull detail while the panel is open.
  const [refetchToken, setRefetchToken] = useState(0);
  const { subscribe } = usePlatformEvents();

  // Subscribe to the selected run at FULL detail (runId filters are untiered).
  // The panel renders from the REST snapshot, so we use events to drive a
  // debounced re-fetch — a live run's steps update while it's watched, and the
  // filter is dropped automatically (coalesced `set`) when the panel closes.
  useEffect(() => {
    if (!run?.runId || !runSpaceId) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const unsub = subscribe(
      [{ eventType: 'agent.run.*', runId: run.runId }],
      () => {
        if (timer) return; // debounce a burst of events into one refetch
        timer = setTimeout(() => { timer = undefined; setRefetchToken((t) => t + 1); }, 600);
      },
    );
    return () => { if (timer) clearTimeout(timer); unsub(); };
  }, [run?.runId, runSpaceId, subscribe]);

  // Clear stale detail when the selection changes (not on live refetch, which
  // keeps the same runId — so the panel doesn't flash empty mid-stream).
  useEffect(() => { setFetchedRun(null); }, [run?.runId]);

  // Fetch full run detail when a run is selected, and re-fetch as live events
  // arrive (refetchToken). Skipped when we don't have the run's owning space —
  // the list payload still renders.
  useEffect(() => {
    const runId = run?.runId;
    if (!runId || !runSpaceId) {
      return;
    }
    let cancelled = false;
    spaceApi(runSpaceId).runs.get(runId)
      .then((data) => { if (!cancelled) setFetchedRun(data); })
      .catch(() => { /* fall back to prop data */ });
    return () => { cancelled = true; };
  }, [run?.runId, runSpaceId, refetchToken]);

  const copyRunId = useCallback(() => {
    if (!run) return;
    navigator.clipboard.writeText(run.runId);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 1500);
  }, [run]);

  const copyResponse = useCallback(() => {
    const text = run?.response ?? '';
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedResponse(true);
    setTimeout(() => setCopiedResponse(false), 1500);
  }, [run]);

  const copyAllSteps = useCallback(() => {
    if (!run) return;
    const allSteps: RunStepResponse[] = run.steps ?? [];
    if (allSteps.length === 0) return;

    const firstStepTime = new Date(allSteps[0].timestamp).getTime();
    const startTime = Number.isFinite(firstStepTime) ? firstStepTime : new Date(run.createdAt).getTime();
    const lines: string[] = [];

    lines.push(`Run: ${run.runId}`);
    if (run.agentName) lines.push(`Agent: ${run.agentName} (${run.agentId})`);
    else if (run.agentId) lines.push(`Agent: ${run.agentId}`);
    lines.push(`Status: ${run.status}`);
    if (run.durationMs) lines.push(`Duration: ${(run.durationMs / 1000).toFixed(1)}s`);
    lines.push(`Created: ${run.createdAt}`);
    lines.push('');
    lines.push(`--- Execution Trace (${allSteps.length} steps) ---`);

    for (const step of allSteps) {
      const rawElapsedMs = new Date(step.timestamp).getTime() - startTime;
      const elapsed = Number.isFinite(rawElapsedMs) ? (rawElapsedMs / 1000).toFixed(1) : '?';
      const duration = step.durationMs != null && Number.isFinite(step.durationMs) ? ` (${(step.durationMs / 1000).toFixed(1)}s)` : '';
      lines.push('');

      if (step.type === 'reasoning') {
        lines.push(`[${elapsed}s] Reasoning${duration}`);
        if (step.text) lines.push(step.text);
        if (step.textUrl) lines.push(`[offloaded: ${step.textUrl}]`);
      } else if (step.type === 'narration') {
        lines.push(`[${elapsed}s] Narration${duration}`);
        if (step.text) lines.push(step.text);
        if (step.textUrl) lines.push(`[offloaded: ${step.textUrl}]`);
      } else if (step.type === 'tool_call') {
        const name = step.toolDisplayName ?? step.toolName ?? 'unknown';
        lines.push(`[${elapsed}s] Tool Call: ${name}${duration}`);
        if (step.input) lines.push(`Input: ${JSON.stringify(step.input, null, 2)}`);
        if (step.error) lines.push(`Error: ${step.error}`);
        else if (step.output) lines.push(`Output: ${step.output}`);
        if (step.outputUrl) lines.push(`[offloaded: ${step.outputUrl}]`);
      }
    }

    const responseText = run.response;
    if (responseText) {
      lines.push('');
      lines.push('--- Response ---');
      lines.push(responseText);
    }
    if (run.error) {
      lines.push('');
      lines.push('--- Error ---');
      lines.push(run.error);
    }

    navigator.clipboard.writeText(lines.join('\n'));
    setCopiedSteps(true);
    setTimeout(() => setCopiedSteps(false), 1500);
  }, [run]);

  const canRetry =
    !!runSpaceId &&
    (run?.status === 'failed' || run?.status === 'error' || run?.status === 'cancelled');

  const handleRetry = useCallback(async () => {
    if (!run || retrying || !runSpaceId) return;
    setRetrying(true);
    setRetryError(null);
    try {
      await spaceApi(runSpaceId).runs.retry(run.runId);
    } catch (err) {
      setRetryError(err instanceof Error ? err.message : 'Retry failed');
    } finally {
      setRetrying(false);
    }
  }, [run, retrying, runSpaceId]);

  if (!run) return null;

  const durationSec = run.durationMs ? Math.round(run.durationMs / 1000) : undefined;
  const steps: RunStepResponse[] = fetchedRun?.steps ?? run.steps ?? [];
  const responseText = fetchedRun?.response ?? run.response;
  const runtime = fetchedRun?.runtime ?? run.runtime;
  const firstStepTime = steps.length > 0 ? new Date(steps[0].timestamp).getTime() : NaN;
  const runStartTime = Number.isFinite(firstStepTime)
    ? firstStepTime
    : new Date(run.createdAt).getTime();
  const processedSteps = processSteps(steps, runStartTime);

  return (
    <div className="h-full overflow-y-auto p-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="flex items-center gap-2 min-w-0">
          <h2 className="text-base font-mono truncate">{run.runId}</h2>
          <button
            type="button"
            className="p-1 rounded text-muted-foreground hover:text-foreground"
            onClick={copyRunId}
            title="Copy run ID"
          >
            {copiedId ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          </button>
        </div>
        <button
          type="button"
          className="p-1 rounded text-muted-foreground hover:text-foreground shrink-0"
          onClick={onClose}
          title="Close panel"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="flex items-center gap-2 flex-wrap text-sm text-muted-foreground mb-4">
        {run.agentName && runSpaceId && (
          <Link
            to={routes.console.spaceAgent(runSpaceId, run.agentId)}
            className="text-primary-600 hover:underline inline-flex items-center gap-1"
          >
            {run.agentName}
            <ExternalLink className="h-3 w-3" />
          </Link>
        )}
        {run.agentName && !runSpaceId && (
          <span>{run.agentName}</span>
        )}
        {!run.agentName && run.agentId && runSpaceId && (
          <Link
            to={routes.console.spaceAgent(runSpaceId, run.agentId)}
            className="text-primary-600 hover:underline inline-flex items-center gap-1 font-mono text-xs"
          >
            {run.agentId}
            <ExternalLink className="h-3 w-3" />
          </Link>
        )}
        {!run.agentName && run.agentId && !runSpaceId && (
          <span className="font-mono text-xs">{run.agentId}</span>
        )}
        {durationSec !== undefined && (
          <span className="flex items-center gap-1 text-xs">
            <Clock className="h-3 w-3" />
            {formatElapsed(durationSec)}
          </span>
        )}
        <Badge variant={getStatusVariant(run.status)}>{run.status}</Badge>
        {canRetry && (
          <button
            type="button"
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-700 hover:bg-primary-200 dark:bg-primary-900/30 dark:text-primary-400 dark:hover:bg-primary-900/50 disabled:opacity-50"
            onClick={handleRetry}
            disabled={retrying}
            title="Retry this run"
          >
            <RotateCw className={`h-3 w-3 ${retrying ? 'animate-spin' : ''}`} />
            {retrying ? 'Retrying…' : 'Retry'}
          </button>
        )}
        {retryError && (
          <span className="text-xs text-red-600 dark:text-red-400">{retryError}</span>
        )}
      </div>

      {/* Metadata bar */}
      <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap border-b border-border pb-3">
        <span>
          Created {formatRelativeTime(run.createdAt)}{' '}
          <span className="text-muted-foreground/60">({formatFullDate(run.createdAt)})</span>
        </span>
        {run.usage && (
          <span className="flex items-center gap-1">
            <Zap className="h-3 w-3" />
            {run.usage.inputTokens.toLocaleString()} in / {run.usage.outputTokens.toLocaleString()} out / {run.usage.totalTokens.toLocaleString()} total
          </span>
        )}
        {hasBuildInfo(runtime) && <BuildInfo info={runtime} />}
        {run.currentIteration !== undefined && run.currentIteration > 0 && (
          <span>{run.currentIteration} iteration{run.currentIteration !== 1 ? 's' : ''}</span>
        )}
      </div>

      {/* Content */}
      <div className="mt-4 space-y-4">
        {/* Execution trace */}
        {processedSteps.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-sm font-medium">
                Execution Trace ({steps.length} steps)
              </h3>
              <button
                type="button"
                className="p-1 rounded text-muted-foreground hover:text-foreground"
                onClick={copyAllSteps}
                title="Copy all steps"
              >
                {copiedSteps ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              </button>
            </div>
            <div className="space-y-2">
              {processedSteps.map((ps, idx) => {
                if (ps.kind === 'subagent-group') {
                  return <SubAgentGroupBlock key={`sag-${idx}`} subAgents={ps.subAgents!} elapsedMs={ps.elapsedMs} runSpaceId={runSpaceId} />;
                }
                const borderColor = ps.kind === 'narration'
                  ? 'border-l-accent-narration-border/40'
                  : ps.kind === 'reasoning'
                    ? 'border-l-accent-reasoning-border/40'
                    : 'border-l-accent-tool-border/40';
                return (
                  <div key={`${ps.step!.type}-${ps.step!.toolCallId ?? idx}`} className={`rounded-lg border border-l-2 ${borderColor} bg-card p-2.5`}>
                    <StepBlock
                      step={ps.step!}
                      elapsedMs={ps.elapsedMs}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Response */}
        {responseText && (
          <div className="rounded-lg border border-l-2 border-l-accent-response-border/40 bg-card p-3">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-sm font-medium">Response</h3>
              <button
                type="button"
                className="p-1 rounded text-muted-foreground hover:text-foreground"
                onClick={copyResponse}
                title="Copy response"
              >
                {copiedResponse ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              </button>
            </div>
            <div className="rounded bg-muted/50 p-3 text-sm">
              <MarkdownContent>{responseText}</MarkdownContent>
              {run.isStreaming && (
                <span className="inline-block w-1.5 h-3.5 bg-foreground/60 animate-pulse ml-0.5 align-text-bottom" />
              )}
            </div>
          </div>
        )}

        {/* Error */}
        {run.error && (
          <div>
            <h3 className="text-sm font-medium text-red-600 mb-2">Error</h3>
            <div className="rounded bg-red-50 dark:bg-red-900/30 p-3 text-sm text-red-700 dark:text-red-400 whitespace-pre-wrap break-words">
              {run.error}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

// ─── Runtime / build inline chip ────────────────────────────────────────────

function BuildInfo({ info }: { info: RunRuntime & { version: string; build: string } }) {
  const buildDate = parseBuildTime(info.build);
  return (
    <span className="flex items-center gap-1">
      <span>Runtime</span>
      <span className="font-mono">v{info.version}</span>
      <span className="text-muted-foreground/60">·</span>
      <span className="font-mono">{info.build}</span>
      {buildDate && (
        <span
          className="text-muted-foreground/60"
          title={buildDate.toLocaleString()}
        >
          (built {formatRelativeTime(buildDate.toISOString())})
        </span>
      )}
    </span>
  );
}

// ─── Offloaded text ─────────────────────────────────────────────────────────

function OffloadedText({ url, autoLoad }: { url: string; autoLoad?: boolean }) {
  const [text, setText] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchOffloadedContent(url)
      .then(setText)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [url]);

  useEffect(() => {
    if (autoLoad && !text && !loading && !error) {
      load();
    }
  }, [autoLoad, text, loading, error, load]);

  if (text) {
    if (isJsonLike(text)) {
      return <JsonContent maxHeight="16rem" showHeader={false}>{text}</JsonContent>;
    }
    return (
      <div className="max-h-60 overflow-auto rounded bg-muted/50 p-2">
        <MarkdownContent>{text}</MarkdownContent>
      </div>
    );
  }

  return (
    <button
      type="button"
      className="text-xs text-primary-600 hover:underline flex items-center gap-1"
      onClick={load}
      disabled={loading}
    >
      {loading && <Loader2 className="h-3 w-3 animate-spin" />}
      {error ? `Error: ${error} — retry` : 'Load full content'}
    </button>
  );
}

// ─── Sub-agent group block ──────────────────────────────────────────────────

function SubAgentGroupBlock({ subAgents, elapsedMs, runSpaceId }: { subAgents: SubAgentRunInfo[]; elapsedMs: number; runSpaceId?: string }) {
  return (
    <div className="rounded-lg border border-l-2 border-l-accent-subagent-group-border/40 bg-card p-3 space-y-2">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Users className="h-3.5 w-3.5 shrink-0 text-accent-subagent-group-fg" />
        <span className="font-medium">Sub-Agents</span>
        <span className="text-xs">{subAgents.length} agent{subAgents.length !== 1 ? 's' : ''}</span>
        <TraceTime elapsedMs={elapsedMs} />
      </div>
      <div className="ml-4 space-y-2">
        {subAgents.map((sa, idx) => (
          <SubAgentRow key={`${sa.agentName}-${idx}`} agent={sa} runSpaceId={runSpaceId} />
        ))}
      </div>
    </div>
  );
}

function SubAgentRow({ agent, runSpaceId }: { agent: SubAgentRunInfo; runSpaceId?: string }) {
  const [expanded, setExpanded] = useState(false);
  const [copiedRunId, setCopiedRunId] = useState(false);
  const [childSteps, setChildSteps] = useState<RunStepResponse[] | null>(null);
  const [childLoading, setChildLoading] = useState(false);
  const [childError, setChildError] = useState<string | null>(null);
  const statusVariant = agent.status === 'completed' ? 'success' : agent.status === 'failed' ? 'error' : 'warning';
  const duration = agent.durationMs != null && Number.isFinite(agent.durationMs) ? `${(agent.durationMs / 1000).toFixed(1)}s` : null;

  const hasExpandableContent = agent.responseText || agent.error || agent.childRunId || agent.prompt;

  const handleExpand = useCallback(() => {
    setExpanded((prev) => {
      const next = !prev;
      // Fetch child run steps on first expand if we have a childRunId and the
      // owning space (sub-agents inherit the parent run's space). Skipped
      // gracefully when runSpaceId isn't resolved.
      if (next && agent.childRunId && runSpaceId && !childSteps && !childLoading) {
        setChildLoading(true);
        setChildError(null);
        spaceApi(runSpaceId).runs.get(agent.childRunId)
          .then((data: { steps?: RunStepResponse[] }) => setChildSteps(data.steps ?? []))
          .catch((err: unknown) => setChildError(err instanceof Error ? err.message : 'Failed to load sub-agent trace'))
          .finally(() => setChildLoading(false));
      }
      return next;
    });
  }, [agent.childRunId, childSteps, childLoading, runSpaceId]);

  const copyChildRunId = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!agent.childRunId) return;
    navigator.clipboard.writeText(agent.childRunId);
    setCopiedRunId(true);
    setTimeout(() => setCopiedRunId(false), 1500);
  }, [agent.childRunId]);

  return (
    <div>
      <div
        className="flex items-center gap-2 text-sm cursor-pointer text-muted-foreground hover:text-foreground"
        onClick={handleExpand}
        role="button"
      >
        {hasExpandableContent ? (
          expanded
            ? <ChevronDown className="h-3 w-3 shrink-0" />
            : <ChevronRight className="h-3 w-3 shrink-0" />
        ) : (
          <div className="w-3" />
        )}
        <Bot className="h-3.5 w-3.5 shrink-0 text-accent-subagent-fg" />
        <span className="font-medium text-foreground">{agent.agentName}</span>
        {duration && <span className="text-xs">{duration}</span>}
        <Badge variant={statusVariant}>{agent.status}</Badge>
        {agent.toolCallCount != null && agent.toolCallCount > 0 && (
          <span className="text-xs">{agent.toolCallCount} tool call{agent.toolCallCount !== 1 ? 's' : ''}</span>
        )}
      </div>

      {expanded && (
        <div className="ml-6 mt-1.5 space-y-2">
          {/* Child run ID */}
          {agent.childRunId && (
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-mono text-muted-foreground/70 truncate">{agent.childRunId}</span>
              <button
                type="button"
                className="p-0.5 rounded text-muted-foreground hover:text-foreground"
                onClick={copyChildRunId}
                title="Copy sub-agent run ID"
              >
                {copiedRunId ? <Check className="h-2.5 w-2.5" /> : <Copy className="h-2.5 w-2.5" />}
              </button>
            </div>
          )}

          {/* Prompt */}
          {agent.prompt && (
            <div className="rounded bg-muted/50 p-2 text-xs text-muted-foreground">
              <span className="text-[10px] font-medium uppercase text-muted-foreground/60 block mb-0.5">Prompt</span>
              <div className="max-h-20 overflow-auto whitespace-pre-wrap">{agent.prompt}</div>
            </div>
          )}

          {/* Error */}
          {agent.error && (
            <div className="rounded bg-red-50 dark:bg-red-900/30 p-2 text-xs text-red-700 dark:text-red-400">
              {agent.error}
            </div>
          )}

          {/* Response */}
          {agent.responseText && (
            <div className="rounded bg-muted/50 p-2">
              <span className="text-[10px] font-medium uppercase text-muted-foreground/60 block mb-0.5">Response</span>
              <div className="max-h-40 overflow-auto text-sm">
                <MarkdownContent>{agent.responseText}</MarkdownContent>
              </div>
            </div>
          )}

          {/* Inline execution trace */}
          {agent.childRunId && (
            <div>
              {childLoading && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground py-1">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Loading execution trace...
                </div>
              )}
              {childError && (
                <div className="text-xs text-red-600 dark:text-red-400 py-1">{childError}</div>
              )}
              {childSteps && childSteps.length > 0 && (
                <ChildExecutionTrace steps={childSteps} runSpaceId={runSpaceId} />
              )}
              {childSteps && childSteps.length === 0 && !childLoading && (
                <div className="text-xs text-muted-foreground/60 py-1">No execution steps recorded</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Renders a child run's execution trace inline using the same step rendering logic. */
function ChildExecutionTrace({ steps, runSpaceId }: { steps: RunStepResponse[]; runSpaceId?: string }) {
  const firstStepTime = steps.length > 0 ? new Date(steps[0].timestamp).getTime() : 0;
  const startTime = Number.isFinite(firstStepTime) ? firstStepTime : 0;
  const processed = processSteps(steps, startTime);

  return (
    <div className="space-y-1.5">
      <div className="text-[10px] font-medium uppercase text-muted-foreground/60">
        Execution Trace ({steps.length} steps)
      </div>
      <div className="space-y-1.5">
        {processed.map((ps, idx) => {
          if (ps.kind === 'subagent-group') {
            return <SubAgentGroupBlock key={`sag-${idx}`} subAgents={ps.subAgents!} elapsedMs={ps.elapsedMs} runSpaceId={runSpaceId} />;
          }
          const borderColor = ps.kind === 'narration'
            ? 'border-l-accent-narration-border/40'
            : ps.kind === 'reasoning'
              ? 'border-l-accent-reasoning-border/40'
              : 'border-l-accent-tool-border/40';
          return (
            <div key={`${ps.step!.type}-${ps.step!.toolCallId ?? idx}`} className={`rounded-lg border border-l-2 ${borderColor} bg-card p-2`}>
              <StepBlock step={ps.step!} elapsedMs={ps.elapsedMs} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Step block sub-component ───────────────────────────────────────────────

function StepBlock({ step, elapsedMs }: { step: RunStepResponse; elapsedMs: number }) {
  const [expanded, setExpanded] = useState(false);

  if (step.type === 'reasoning') {
    const content = step.text ?? '';
    const firstLine = content.split('\n')[0];
    const needsExpand = content.length > 120 || content.includes('\n');

    return (
      <div className="text-sm space-y-1">
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <Brain className="h-3.5 w-3.5 shrink-0 text-accent-reasoning-fg/70" />
          <span className="font-medium">Reasoning</span>
          <TraceTime elapsedMs={elapsedMs} durationMs={step.durationMs} />
        </div>
        {content && (
          <div className={`rounded bg-muted/50 p-2 ${expanded ? 'max-h-64 overflow-auto' : 'overflow-hidden'}`}>
            {expanded ? (
              <MarkdownContent>{content}</MarkdownContent>
            ) : (
              <div className="[&_*]:!my-0 [&_*]:!leading-tight">
                <MarkdownContent>{firstLine}</MarkdownContent>
              </div>
            )}
          </div>
        )}
        {step.textUrl && <OffloadedText url={step.textUrl} autoLoad />}
        {needsExpand && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
          >
            {expanded ? 'Show less' : 'Show more'}
          </button>
        )}
      </div>
    );
  }

  if (step.type === 'narration') {
    return (
      <div className="text-sm space-y-1">
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <MessageSquare className="h-3.5 w-3.5 shrink-0 text-accent-narration-fg/70" />
          <span className="font-medium">Narration</span>
          <TraceTime elapsedMs={elapsedMs} durationMs={step.durationMs} />
        </div>
        {step.text && (
          <div className="rounded bg-muted/50 p-2 max-h-96 overflow-auto text-muted-foreground">
            <MarkdownContent>{step.text}</MarkdownContent>
          </div>
        )}
        {step.textUrl && <OffloadedText url={step.textUrl} autoLoad />}
      </div>
    );
  }

  // tool_call — skip sub-agent tools (handled by processSteps)
  if (step.toolName?.startsWith('__subagent_')) return null;

  return (
    <ToolCallCard
      toolName={step.toolName!}
      toolCallId={step.toolCallId!}
      args={step.input ?? {}}
      result={step.error ?? step.output}
      isError={!!step.error}
      durationMs={step.durationMs}
      outputUrl={step.outputUrl}
      compact
      traceTime={<TraceTime elapsedMs={elapsedMs} durationMs={step.durationMs} />}
    />
  );
}
